import { RuleGroup } from "@/types/rule.types.ts";
import {
  getWorkflowRuleGroups,
  toWorkflowRuleGroupsPayload,
  workflowRuleGroupsSchema,
} from "../workflow-rule-groups.ts";
import { WorkflowEditActionPerformanceReviewEnrollFormData } from "@/pages/workflows/edit/actions/WorkflowEditActionPerformanceReviewEnrollModal/types";
import { WorkflowPerformanceReviewEnrollReviewerTypesSection } from "@/pages/workflows/edit/actions/WorkflowEditActionPerformanceReviewEnrollModal/WorkflowPerformanceReviewEnrollReviewerTypesSection";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import {
  usePerformanceReviewSetup,
  usePerformanceReviewsIsRunningSearchInline,
} from "@/queries/performance-reviews/use-performance-reviews.query";
import { useWorkflowUpdateBlockActionPerformanceReviewEnroll } from "@/queries/workflows/workflow.query.ts";
import {
  ApiPerformanceReviewReviewerTypes,
  getPerformanceReviewParticipantGroups,
} from "@/types/performance-review.types.ts";
import { RuleConditionType, RuleConditionsItems } from "@/types/role.types.ts";
import {
  ApiWorkflowBlockActionPerformanceReviewEnrollSave,
  ApiWorkflowBlockActionSavePayload,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Input,
  Modal,
  ModalProps,
  Select,
  Skeleton,
  Stack,
  TabItem,
  Tabs,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "@/pages/workflows/edit/actions/WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
} from "@/pages/workflows/workflow-hook.tsx";

export interface WorkflowEditActionPerformanceReviewEnrollModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
}

const REVIEWER_TYPE_SORT_ORDER: Record<ApiPerformanceReviewReviewerTypes, number> = {
  [ApiPerformanceReviewReviewerTypes.myself]: 0,
  [ApiPerformanceReviewReviewerTypes.manager]: 1,
  [ApiPerformanceReviewReviewerTypes.upward]: 2,
  [ApiPerformanceReviewReviewerTypes.peer]: 3,
  [ApiPerformanceReviewReviewerTypes.custom]: 4,
};

export const WorkflowEditActionPerformanceReviewEnrollModal = ({
  workflowVersion,
  block,
  ...props
}: WorkflowEditActionPerformanceReviewEnrollModalProps) => {
  const { close } = useModals();
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const { id } = { ...props };
  const initializedReviewIdRef = useRef<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const {
    options: performanceReviewOptions,
    search,
    isFetching,
    isLoading,
  } = usePerformanceReviewsIsRunningSearchInline(true);

  const schema = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    performance_review_id: yup
      .number()
      .required(t("errors:field_required"))
      .typeError(t("errors:field_required"))
      .nullable(),
    reviewer_types: yup
      .array()
      .of(
        yup.object({
          id: yup.number().required(),
          reviewer_type: yup
            .mixed<ApiPerformanceReviewReviewerTypes>()
            .oneOf(Object.values(ApiPerformanceReviewReviewerTypes))
            .required(),
          title: yup.string().defined().nullable(),
          template_title: yup.string().defined().nullable(),
          form_id: yup.number().defined().nullable(),
          participants_rule_groups: workflowRuleGroupsSchema(),
        })
      )
      .required(),
  });

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<WorkflowEditActionPerformanceReviewEnrollFormData>({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      performance_review_id: block.action_performance_review_enroll?.review_id ?? null,
      reviewer_types: [],
    },
  });

  const { replace } = useFieldArray({
    control,
    name: "reviewer_types",
  });

  const updateBlockAction = useWorkflowUpdateBlockActionPerformanceReviewEnroll(control);

  const selectedPerformanceReviewId = useWatch({
    name: "performance_review_id",
    control,
  });

  const reviewerTypes =
    useWatch({
      name: "reviewer_types",
      control,
    }) ?? [];

  const { data: performanceReviewSettings, isLoading: isPerformanceReviewSettingsLoading } =
    usePerformanceReviewSetup(
      selectedPerformanceReviewId ?? undefined,
      !!selectedPerformanceReviewId
    );

  const allReviewerTypeItems = useMemo(() => {
    if (!performanceReviewSettings) return [];

    return performanceReviewSettings.reviewer_types
      .map((reviewerType) => ({
        id: reviewerType.id,
        reviewer_type: reviewerType.type,
        title: reviewerType.title,
        template_title: reviewerType.template_title,
        form_id: reviewerType.form?.id ?? null,
      }))
      .sort((itemA, itemB) => {
        const orderA = REVIEWER_TYPE_SORT_ORDER[itemA.reviewer_type];
        const orderB = REVIEWER_TYPE_SORT_ORDER[itemB.reviewer_type];
        if (orderA !== orderB) return orderA - orderB;
        return itemA.id - itemB.id;
      });
  }, [performanceReviewSettings]);

  const performanceReviewSelectOptions = useMemo(() => {
    const options = [...performanceReviewOptions];
    const selectedId = selectedPerformanceReviewId ?? performanceReviewSettings?.id ?? null;
    const selectedTitle = performanceReviewSettings?.title ?? null;

    if (selectedId == null || !selectedTitle) return options;

    const hasSelectedOption = options.some((item) => Number(item.id) === selectedId);
    if (hasSelectedOption) return options;

    return [{ id: selectedId, title: selectedTitle }, ...options];
  }, [
    performanceReviewOptions,
    performanceReviewSettings?.id,
    performanceReviewSettings?.title,
    selectedPerformanceReviewId,
  ]);

  const hasSelectedPerformanceReviewOption = useMemo(() => {
    if (selectedPerformanceReviewId == null) return true;

    return performanceReviewSelectOptions.some(
      (item) => Number(item.id) === selectedPerformanceReviewId
    );
  }, [performanceReviewSelectOptions, selectedPerformanceReviewId]);

  const shouldShowPerformanceReviewSelectSkeleton = Boolean(
    selectedPerformanceReviewId != null &&
    !hasSelectedPerformanceReviewOption &&
    (isLoading || isFetching || isPerformanceReviewSettingsLoading)
  );

  const participantRuleTypeTitles = useMemo(() => {
    return new Map(RuleConditionsItems().map((item) => [String(item.id), item.title]));
  }, [t]);

  const transformableParticipantsRuleTitles = useMemo(() => {
    const rules = performanceReviewSettings
      ? getPerformanceReviewParticipantGroups(performanceReviewSettings).flatMap(
          (group) => group.rules
        )
      : [];
    const titles: string[] = [];
    const usedTitles = new Set<string>();

    for (const rule of rules) {
      if (rule.condition_type === RuleConditionType.employee) continue;

      const title =
        participantRuleTypeTitles.get(String(rule.condition_type)) ?? String(rule.condition_type);

      if (usedTitles.has(title)) continue;
      usedTitles.add(title);
      titles.push(title);
    }

    return titles;
  }, [participantRuleTypeTitles, performanceReviewSettings]);

  const savedRulesByReviewerTypeId = useMemo(() => {
    const reviewEnrollAction = block.action_performance_review_enroll;
    if (!reviewEnrollAction) return new Map<number, RuleGroup[]>();
    if (reviewEnrollAction.review_id !== selectedPerformanceReviewId)
      return new Map<number, RuleGroup[]>();

    return new Map<number, RuleGroup[]>(
      reviewEnrollAction.reviewer_types.map((reviewerType) => [
        reviewerType.reviewer_type_id,
        getWorkflowRuleGroups(
          reviewerType.participants_rule_groups,
          reviewerType.participants_rules
        ),
      ])
    );
  }, [block.action_performance_review_enroll, selectedPerformanceReviewId]);

  const hasOnlyEmployeeParticipantsRules = useMemo(() => {
    const rules = performanceReviewSettings
      ? getPerformanceReviewParticipantGroups(performanceReviewSettings).flatMap(
          (group) => group.rules
        )
      : [];
    if (!rules.length) return false;
    return rules.every((rule) => rule.condition_type === RuleConditionType.employee);
  }, [performanceReviewSettings]);

  const shouldShowRulesConversionWarning = Boolean(
    performanceReviewSettings != null &&
    getPerformanceReviewParticipantGroups(performanceReviewSettings).some(
      (group) => group.rules.length > 0
    ) &&
    !hasOnlyEmployeeParticipantsRules
  );

  useEffect(() => {
    if (selectedPerformanceReviewId == null) {
      initializedReviewIdRef.current = null;
      replace([]);
      return;
    }

    if (!allReviewerTypeItems.length) return;
    if (initializedReviewIdRef.current === selectedPerformanceReviewId) return;

    const mappedReviewerTypes = allReviewerTypeItems.map((reviewerType) => {
      const savedRules = savedRulesByReviewerTypeId.get(reviewerType.id);
      const canConfigureParticipants =
        reviewerType.reviewer_type === ApiPerformanceReviewReviewerTypes.peer ||
        reviewerType.reviewer_type === ApiPerformanceReviewReviewerTypes.custom;

      if (!canConfigureParticipants) {
        return {
          ...reviewerType,
          participants_rule_groups: [],
        };
      }

      if (savedRules != null) {
        return {
          ...reviewerType,
          participants_rule_groups: savedRules,
        };
      }

      return {
        ...reviewerType,
        participants_rule_groups: [],
      };
    });

    replace(mappedReviewerTypes);
    initializedReviewIdRef.current = selectedPerformanceReviewId;
  }, [allReviewerTypeItems, replace, savedRulesByReviewerTypeId, selectedPerformanceReviewId]);

  const handleSave = handleSubmit(async (data) => {
    if (selectedPerformanceReviewId == null) return;

    const blockData: ApiWorkflowBlockActionPerformanceReviewEnrollSave = {
      review_id: selectedPerformanceReviewId,
      reviewer_types: data.reviewer_types.map((reviewerType) => ({
        reviewer_type_id: reviewerType.id,
        participants_rule_groups: toWorkflowRuleGroupsPayload(
          reviewerType.participants_rule_groups
        ),
      })),
    };

    const payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionPerformanceReviewEnrollSave> =
      {
        title: data.title,
        ...getWorkflowActionTimingPayload(data),
        conditions: conditions.map((condition) => ({ ...condition })),
        block_data: blockData,
      };

    try {
      await updateBlockAction.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload,
      });
      close(id);
    } catch {
      // handled by hook
    }
  });

  const isSubmitDisabled =
    selectedPerformanceReviewId == null ||
    isPerformanceReviewSettingsLoading ||
    updateBlockAction.isPending;

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("workflow:action_type_performance_review_add_employee")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <Button
            className={"w-full"}
            size={"lg"}
            type={"submit"}
            onClick={handleSave}
            disabled={isSubmitDisabled}
          >
            {t("common:save")}
          </Button>
          <Button className={"w-full"} size={"lg"} variant={"secondary"} onClick={() => close(id)}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSave}>
        <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
          <TabItem title={t("performance_review:performance_review")}>
            <Stack className={"py-6"}>
              {shouldShowPerformanceReviewSelectSkeleton ? (
                <Stack gap={"xs"}>
                  <Skeleton className={"h-4 w-40"} />
                  <Skeleton className={"h-12 w-full"} />
                </Stack>
              ) : (
                <Controller
                  control={control}
                  name={"performance_review_id"}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label={t("performance_review:performance_review")}
                      value={value}
                      emptyTitle={t("common:select")}
                      searchable
                      required
                      loadOptionsOnOpen
                      onSearchOptions={search}
                      searchOptions={performanceReviewSelectOptions}
                      onChange={(nextValue) => {
                        const normalizedValue =
                          nextValue == null || nextValue === "" ? null : Number(nextValue);
                        setValue("performance_review_id", normalizedValue, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        onChange(normalizedValue);
                      }}
                      isLoading={isLoading || isFetching}
                      error={errors.performance_review_id?.message}
                    />
                  )}
                />
              )}
              <WorkflowPerformanceReviewEnrollReviewerTypesSection
                control={control}
                setValue={setValue}
                reviewerTypes={reviewerTypes}
                shouldShowRulesConversionWarning={shouldShowRulesConversionWarning}
                transformableParticipantsRuleTitles={transformableParticipantsRuleTitles}
              />
            </Stack>
          </TabItem>

          <TabItem title={t("workflow:conditions")}>
            <Stack gap={"lg"} className={"py-6"}>
              <Stack>
                <Title header={t("workflow:block")} size={"xs"} />
                <Input
                  label={t("common:title")}
                  type="text"
                  {...register("title")}
                  error={errors.title?.message}
                />
              </Stack>

              <WorkflowActionTimingEditor
                control={control}
                register={register}
                errors={errors}
                triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
              />

              <WorkflowBlockConditionsList
                conditions={conditions}
                onAdd={handleAddCondition}
                onEdit={handleEditCondition}
                onDelete={handleDeleteCondition}
              />
            </Stack>
          </TabItem>
        </Tabs>
      </form>
    </Modal>
  );
};
